import Observer from '@researchgate/react-intersection-observer';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import React, {useMemo, useState} from 'react';

import {PAGE_TYPE_SUBS_LANDING_PAGE, UI_REGION} from '@udemy/browse-utils';
import {DiscoveryUnit, DiscoveryUnitItem} from '@udemy/discovery-api';
import {useI18n} from '@udemy/i18n';
import {DiscoveryUnitsContainer} from '@udemy/react-discovery-units';
import {
    CMSConnectedPartnersBanner,
    DynamicWebContentPageType,
} from '@udemy/shared-one-udemy-components';
import {
    ConsumerSubscriptionPlan,
    DateIntervalType,
    useConsumerSubscriptionPlan,
} from '@udemy/subscription-graphql';
import {udLink} from '@udemy/ud-data';

import {WindowShoppingUnit} from 'src/components/window-shopping-unit/window-shopping-unit.react-component';

import {CollectionTitle} from './components/collection-title/collection-title.react-component';
import {ConsumerSubscriptionStickyHeader} from './components/consumer-subscription-sticky-header/consumer-subscription-sticky-header.react-component';
import {PersonalPlanBannerWrapper} from './components/personal-plan-banner/personal-plan-banner-wrapper.react-component';
import {PersonalPlanChecklist} from './components/personal-plan-checklist/personal-plan-checklist.react-component';
import {PersonalPlanFAQ} from './components/personal-plan-faq/personal-plan-faq.react-component';
import {PersonalPlanSubscriptionCTA} from './components/personal-plan-subscription-cta/personal-plan-subscription-cta.react-component';
import {TrustedNumbers} from './components/trusted-numbers/trusted-numbers.react-component';
import {ValueProp} from './components/value-prop/value-prop.react-component';
import {useCoursesPriceRange} from './hooks/useCoursesPriceRange';
import styles from './personal-plan-landing-page-body.module.less';
import {
    useNonCatalogCheckoutSource,
    usePersonalPlanSubscriptionCheckoutEntry,
} from './personal-plan-subscription-checkout-entry-context';

export interface PersonalPlanLandingPageProps {
    isPPLPRevamp: boolean;
}

export const PersonalPlanLandingPageBody = observer(
    ({isPPLPRevamp}: PersonalPlanLandingPageProps) => {
        const i18nApi = useI18n();
        const {gettext} = i18nApi;
        const [isFloatingHeaderVisible, setFloatingHeaderVisibility] = React.useState(false);
        const handleIntersectionChange = React.useCallback((ev) => {
            setFloatingHeaderVisibility(!ev.isIntersecting);
        }, []);

        const valuePropsConfig = useMemo(() => {
            const baseConfig = [
                {
                    id: 'current',
                    slogan: gettext('Current'),
                    heading: gettext('Cutting-edge skills to keep you sharp'),
                    content: gettext(
                        'Learn confidently with up-to-date courses covering in-demand topics like AI for any role, cloud computing certifications, web development, productivity, leadership, design, digital marketing, and more.',
                    ),
                    imgSrc: {
                        mobileSrc: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-1-mobile-v2.jpg',
                        ),
                        desktopSrc: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-1-desktop-v2.jpg',
                        ),
                        desktopSrc2x: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-1-desktop-2x-v2.jpg',
                        ),
                    },
                },
                {
                    id: 'flexible',
                    slogan: gettext('Flexible'),
                    heading: gettext('Freedom to explore and discover'),
                    content: gettext(
                        'Test drive a new subject, switch between courses, or pick and choose the lessons that best fit your needs. Personal Plan gives you the power to control what and how you learn. Plus use the Udemy AI Assistant to get instant answers to your questions while you learn.',
                    ),
                    imgSrc: {
                        mobileSrc: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-2-mobile-v2.jpg',
                        ),
                        desktopSrc: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-2-desktop-v2.jpg',
                        ),
                        desktopSrc2x: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-2-desktop-2x-v2.jpg',
                        ),
                    },
                },
                {
                    id: 'effective',
                    slogan: gettext('Effective'),
                    heading: gettext('Learning designed to help you start doing'),
                    content: gettext(
                        'Gain knowledge through practice with AI Role Play simulations, labs, coding exercises, and certification exam practice tests. Once you finish a course, strut your new skills with a certificate of completion from Udemy or issuers like AWS, Microsoft, Google, CompTIA, and PMI.',
                    ),
                    imgSrc: {
                        mobileSrc: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-3-mobile-v2.jpg',
                        ),
                        desktopSrc: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-3-desktop-v2.jpg',
                        ),
                        desktopSrc2x: udLink.toStorageStaticAsset(
                            'consumer-subscription/pillars/pillars-3-desktop-2x-v2.jpg',
                        ),
                    },
                },
            ];

            const revampConfig = [
                {
                    id: 'career',
                    slogan: gettext('CAREER'),
                    heading: gettext('Careers reimagined'),
                    content: gettext(
                        'Start or advance your career in graphic design, web dev, digital marketing, and more with unlimited access to 26,000+ courses — plus curated learning with Career Accelerators',
                    ),
                    imgSrc: {
                        mobileSrc:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropMobile_1.png',
                        desktopSrc:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_1.png',
                        desktopSrc2x:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_1.png',
                    },
                },
                {
                    id: 'learn',
                    slogan: gettext('LEARN'),
                    heading: gettext('AI skills at the ready'),
                    content: gettext(
                        'Keep ahead with the latest in AI skills including ChatGPT, Microsoft CoPilot, Midjourney, machine learning, deep learning, and more with over 1,300 AI courses included',
                    ),
                    imgSrc: {
                        mobileSrc:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropMobile_2.png',
                        desktopSrc:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_2.png',
                        desktopSrc2x:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_2.png',
                    },
                },
                {
                    id: 'certification',
                    slogan: gettext('CERTIFICATION'),
                    heading: gettext('Certification prep. Career advancement.'),
                    content: gettext(
                        'Prepare for exams in CompTIA, AWS, PMP, and other in-demand certifications, plus take the next step with exam vouchers',
                    ),
                    imgSrc: {
                        mobileSrc:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropMobile_3.png',
                        desktopSrc:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_3.png',
                        desktopSrc2x:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_3.png',
                    },
                },
                {
                    id: 'practice',
                    slogan: gettext('PRACTICE'),
                    heading: gettext('AI-powered learning for the real world'),
                    content: gettext(
                        'Upskill smarter and faster with guided learning from Udemy AI Assistant, conversation coaching from Role Play, and coding exercises that develop your technical skills',
                    ),
                    imgSrc: {
                        mobileSrc:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropMobile_4.png',
                        desktopSrc:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_4.png',
                        desktopSrc2x:
                            'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_4.png',
                    },
                },
            ];

            return isPPLPRevamp ? revampConfig : baseConfig;
        }, [isPPLPRevamp, gettext]);

        // Subscription plan loading and derived state
        const [personalPlanIsLoading, setPersonalPlanIsLoading] = useState(true);
        const personalPlanContext = useConsumerSubscriptionPlan();
        const {consumerSubscriptionPlan, isPersonalPlanSubscriber, renderSubscriptionCTA} =
            useMemo(() => {
                setPersonalPlanIsLoading(personalPlanContext?.isLoading ?? true);
                const isPersonalPlanSubscriber =
                    (personalPlanContext &&
                        'isSubscriberToSpecificPlan' in personalPlanContext &&
                        personalPlanContext.isSubscriberToSpecificPlan) ??
                    false;
                const renderSubscriptionCTA = personalPlanContext && !personalPlanIsLoading;
                const consumerSubscriptionPlan =
                    !personalPlanIsLoading &&
                    !isPersonalPlanSubscriber &&
                    personalPlanContext &&
                    'id' in personalPlanContext
                        ? (personalPlanContext as ConsumerSubscriptionPlan)
                        : undefined;
                return {consumerSubscriptionPlan, isPersonalPlanSubscriber, renderSubscriptionCTA};
            }, [personalPlanContext, personalPlanIsLoading]);

        const {isCoursesPriceRangeLoading, coursesPriceRange} = useCoursesPriceRange();
        const subscriptionCheckoutEntry = usePersonalPlanSubscriptionCheckoutEntry();
        const {source: nonCatalogSource, noticeDataLoading: nonCatalogNoticeDataLoading} =
            useNonCatalogCheckoutSource();

        const collectionCta = renderSubscriptionCTA ? (
            <PersonalPlanSubscriptionCTA
                plan={personalPlanContext}
                planPreference={DateIntervalType.Month}
                className={styles['window-shopping-cta']}
                uiRegion={UI_REGION.SUBS_COLLECTION_SAMPLER}
                showSubtitle={false}
                subscriptionCheckoutEntry={subscriptionCheckoutEntry}
                source={nonCatalogSource}
                noticeDataLoading={nonCatalogNoticeDataLoading}
            />
        ) : null;

        const getUnitComponentAndProps = (unit: Partial<DiscoveryUnit<DiscoveryUnitItem>>) => {
            if (!unit) throw new Error('Missing unit');

            switch (unit.type) {
                case 'bestseller':
                    return {
                        component: WindowShoppingUnit,
                        componentProps: {
                            renderCta: collectionCta,
                        },
                    };
                default:
                    throw new Error(`Missing component for unit type: ${unit.type}`);
            }
        };

        return (
            <>
                {!personalPlanIsLoading && personalPlanContext ? (
                    <ConsumerSubscriptionStickyHeader
                        label={gettext('Personal Plan')}
                        subscriptionContext={personalPlanContext}
                        visible={isFloatingHeaderVisible}
                    />
                ) : undefined}
                <div className={styles.wrapper}>
                    <Observer onChange={handleIntersectionChange}>
                        <div
                            className={classNames(
                                'ud-container',
                                styles['banner-and-stats-wrapper'],
                            )}
                        >
                            <PersonalPlanBannerWrapper isPPLPRevamp={isPPLPRevamp} />
                            <TrustedNumbers
                                className={styles['updated-trusted-numbers']}
                                isPPLPRevamp={isPPLPRevamp}
                            />
                        </div>
                    </Observer>

                    <div
                        className={classNames(
                            'component-margin',
                            styles['updated-partners-banner-wrapper'],
                        )}
                    >
                        <div className="ud-container">
                            <div className={styles['partners']}>
                                <CMSConnectedPartnersBanner
                                    pageType={DynamicWebContentPageType.PersonalPlanLandingPage}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={classNames('ud-container', styles['landing-page'])}>
                        <section className={styles['value-props']}>
                            {valuePropsConfig.map((valueProp) => (
                                <ValueProp
                                    key={valueProp.id}
                                    imgSrc={valueProp.imgSrc}
                                    slogan={valueProp.slogan}
                                    className="component-margin"
                                    heading={valueProp.heading}
                                    content={valueProp.content}
                                    isPPLPRevamp={isPPLPRevamp}
                                />
                            ))}
                            <div className="component-margin">
                                <CollectionTitle
                                    className={styles['collection-title']}
                                    isPPLPRevamp={isPPLPRevamp}
                                />
                                <DiscoveryUnitsContainer
                                    pageType={PAGE_TYPE_SUBS_LANDING_PAGE}
                                    itemCount={6}
                                    getUnitComponentAndProps={getUnitComponentAndProps}
                                />
                            </div>
                        </section>
                    </div>
                    <PersonalPlanChecklist
                        plan={consumerSubscriptionPlan}
                        isSubscriber={isPersonalPlanSubscriber}
                        isCoursesPriceRangeLoading={isCoursesPriceRangeLoading}
                        coursesPriceRange={coursesPriceRange}
                        isPPLPRevamp={isPPLPRevamp}
                    />
                    <div className={styles['faq']}>
                        <PersonalPlanFAQ />
                    </div>
                </div>
            </>
        );
    },
);
